import { ConfirmDestructiveHost } from "@sportsmet/ui";
import type { QueryClient } from "@tanstack/react-query";
import { createRootRouteWithContext, Outlet } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { ErrorModalHost } from "../errors/ErrorModalHost";
import { OnboardingGate } from "../features/onboarding/OnboardingGate";
import { AppShell } from "../shell/AppShell";
import { NotFoundScreen } from "../shell/NotFoundScreen";
import { RouteErrorScreen } from "../shell/RouteErrorScreen";
import { RoutePendingScreen } from "../shell/RoutePendingScreen";

/**
 * What every route's `beforeLoad` and `loader` receives.
 *
 * The query client is passed through the router rather than reached for via a
 * module singleton so a test can hand the whole app a throwaway cache.
 */
export type typeRouterContext = {
  queryClient: QueryClient;
};

const RootLayout = (): ReactNode => (
  <>
    <AppShell>
      <OnboardingGate>
        <Outlet />
      </OnboardingGate>
    </AppShell>
    {/*
      Mounted ONCE, at the root, above every route. It
      portals to the document body and outranks every feature overlay, so an
      error raised while a sheet is open renders in front of that sheet instead
      of behind it where nobody would see it.
    */}
    <ErrorModalHost />
    <ConfirmDestructiveHost />
  </>
);

export const Route = createRootRouteWithContext<typeRouterContext>()({
  component: RootLayout,
  errorComponent: RouteErrorScreen,
  notFoundComponent: NotFoundScreen,
  pendingComponent: RoutePendingScreen,
});
